import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, AxiosResponseHeaders } from "axios";
import { Thunk } from "../../store";
import {
  BackendObtenerEstadoTareas,
  getCurrentDomain,
} from "../../../apis/backend/Api";
import mock_Json from "../../../dataJson/obtenerEstadoTareas.json";
import { formatearMensajeBackend } from "../../../apis/helper/helperApi";
import { TypeStatus } from "../../helpers/constantes";

interface State {
  obtenerEstadoTareas: any[];
  status: TypeStatus;
  isLoading: boolean;
  Success: boolean;
  Mensaje: string | null;
  error: string | null;
}

const initialState: State = {
  obtenerEstadoTareas: [],
  status: "idle",
  isLoading: false,
  Success: false,
  Mensaje: "",
  error: null,
};
interface defaultResponse {
  data: any;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | {};
  config: any;
  request?: any;
}
const defaultResp: defaultResponse = {
  data: undefined,
  status: 0,
  statusText: "",
  headers: {},
  config: undefined,
};
const obtenerEstadoTareasSlice = createSlice({
  name: "obtenerEstadoTareas",
  initialState,
  reducers: {
    setObtenerEstadoTareas: (state, action: PayloadAction<any[]>) => {
      state.obtenerEstadoTareas = action.payload;
    },
    getObtenerEstadoTareas: (state, action: PayloadAction<any[]>) => {
      state.obtenerEstadoTareas = action.payload;
    },
    setClearObtenerEstadoTareas: (state, action: PayloadAction<any[]>) => {
      state = initialState;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStatus: (state, action: PayloadAction<TypeStatus>) => {
      state.status = action.payload;
    },
    setResult: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.Success = action.payload;
    },
    setMensaje: (state, action: PayloadAction<string>) => {
      state.Mensaje = action.payload;
    },
  },
});
export const setBackendObtenerEstadoTareas =
  (): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));

    try {
      const response: AxiosResponse = await BackendObtenerEstadoTareas();

      const isError =
        response.data?.Success === false ||
        (response.data?.ResultadoSolicitud !== undefined &&
          response.data?.ResultadoSolicitud !== 200);

      if (isError) {
        dispatch(setIsLoading(false));
        dispatch(setStatus("failed"));
        dispatch(setSuccess(false));
      } else {
        let arrayUnShift = [...response.data.Data];
        arrayUnShift.unshift(" ");
        dispatch(setObtenerEstadoTareas(arrayUnShift));
        dispatch(setIsLoading(false));
        dispatch(setStatus("succeeded"));
        dispatch(setSuccess(true));
      }
      dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));
      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setSuccess(false));
      dispatch(setStatus("failed"));

      if (error.response?.status === 401) {
        dispatch(setStatus("noAutorice"));
        dispatch(
          setMensaje(
            formatearMensajeBackend(
              "Tu sesión ha expirado. Vuelve a iniciar sesión",
            ),
          ),
        );
      } else if (error.response) {
        // Hay respuesta del servidor, pero es otro error (400, 422, 500, etc.)
        console.error(
          "Error de servidor:",
          error.response.status,
          error.response.data,
        );

        const errorData = error.response.data;
        const mensajeBackend = errorData?.Mensaje;
        const resultadoSolicitud = errorData?.ResultadoSolicitud;

        if (
          mensajeBackend &&
          (resultadoSolicitud !== 200 || errorData?.Success === false)
        ) {
          dispatch(setMensaje(formatearMensajeBackend(mensajeBackend)));
        } else {
          dispatch(
            setMensaje(
              `Error ${error.response.status}: Ocurrió un problema en el servidor.`,
            ),
          );
        }
      } else if (error.request) {
        console.error("Error de conexión:", error.message);
        dispatch(
          setMensaje(
            "No se pudo conectar al servidor. Por favor, revisa tu conexión a internet o tu VPN.",
          ),
        );
      } else {
        console.error("Error de red o solicitud:", error.message);
        dispatch(
          setMensaje(
            "Ocurrió un problema con la red. Por favor, verifica tu conexión.",
          ),
        );
      }
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
      if (getCurrentDomain() === "localhost") {
        dispatch(setIsLoading(true));
        let response: AxiosResponse = defaultResp;
        response.data = mock_Json;

        const mockData: any = mock_Json;
        const isErrorMock =
          mockData?.Success === false ||
          (mockData?.ResultadoSolicitud !== undefined &&
            mockData?.ResultadoSolicitud !== 200);

        if (isErrorMock) {
          dispatch(setIsLoading(false));
          dispatch(setStatus("failed"));
          dispatch(setSuccess(false));
        } else {
          let arrayUnShift = [...mock_Json.Data];
          arrayUnShift.unshift(" ");
          dispatch(setObtenerEstadoTareas(arrayUnShift));
          dispatch(setIsLoading(false));
          dispatch(setStatus("succeeded"));
          dispatch(setSuccess(true));
        }
        dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));
        const mock401 = await import("../../../dataJson/401.json");
        if (mock401.status === 401) {
          dispatch(setStatus("noAutorice"));
          dispatch(
            setMensaje(
              formatearMensajeBackend(
                "Tu sesión ha expirado. Vuelve a iniciar sesión",
              ),
            ),
          );
        }
      }
    }
  };

export const setBackendClearObtenerEstadoTareas =
  (): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));
    try {
      let response: AxiosResponse = defaultResp;
      dispatch(setObtenerEstadoTareas([]));
      dispatch(setIsLoading(false));
      dispatch(setStatus("idle"));
      dispatch(setSuccess(false));
      dispatch(setMensaje(""));
      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setStatus("failed"));
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const {
  setObtenerEstadoTareas,
  getObtenerEstadoTareas,
  setClearObtenerEstadoTareas,
  setIsLoading,
  setStatus,
  setResult,
  setSuccess,
  setMensaje,
} = obtenerEstadoTareasSlice.actions;

export default obtenerEstadoTareasSlice.reducer;
